import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  ReceiptText,
  Calendar,
  DollarSign,
  PoundSterling,
  FileText,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Download,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import { Currency, LocalDate } from "@/components/i18n";
import { useCountry } from "@/contexts/CountryContext";
import DashboardContainer from "../layout/DashboardContainer";
import InvoiceAPIService from "@/services/invoice-api";
import {
  Invoice,
  InvoiceStatus,
  InvoiceStatusLabels,
  InvoiceStatusColors,
} from "@/types/invoice";

const InvoiceCard: React.FC<{
  invoice: Invoice;
  onView: (invoice: Invoice) => void;
  onEdit: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  onDownload: (invoice: Invoice) => void;
}> = ({ invoice, onView, onEdit, onDelete, onDownload }) => {
  const { formatCurrency, formatDate, currentCountry } = useCountry();

  const getStatusBadgeClass = (status: InvoiceStatus) => {
    return InvoiceStatusColors[status];
  };

  return (
    <Card
      className="transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer"
      data-testid={`invoice-card-${invoice.id}`}
      role="button"
      onClick={() => onView(invoice)}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <ReceiptText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">
                {invoice.invoiceNumber || `Invoice #${invoice.id}`}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Due: <LocalDate date={invoice.dueDate} format="medium" />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusBadgeClass(invoice.status)}>
              {InvoiceStatusLabels[invoice.status]}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`invoice-menu-${invoice.id}`}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-foreground"
                  onClick={() => onView(invoice)}
                  data-testid={`view-invoice-${invoice.id}`}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-foreground"
                  onClick={() => onDownload(invoice)}
                  data-testid={`invoice-download-${invoice.id}`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </DropdownMenuItem>
                {invoice.status === "DRAFT" && (
                  <DropdownMenuItem
                    className="text-foreground"
                    onClick={() => onEdit(invoice)}
                    data-testid={`invoice-edit-${invoice.id}`}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => onDelete(invoice)}
                  className="text-destructive"
                  data-testid={`invoice-delete-${invoice.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Subtotal</p>
            <p className="font-semibold">
              <Currency 
                amount={parseFloat(invoice.subtotal.toString()) || 0} 
                currencyCode={currentCountry?.currencyCode}
              />
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Tax</p>
            <p className="font-semibold">
              <Currency 
                amount={parseFloat(invoice.totalTax.toString()) || 0} 
                currencyCode={currentCountry?.currencyCode}
              />
            </p>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="font-bold text-lg">
              <Currency 
                amount={parseFloat(invoice.grandTotal.toString()) || 0} 
                currencyCode={currentCountry?.currencyCode}
              />
            </p>
          </div>
        </div>
        {invoice.notes && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">Notes</p>
            <p className="text-sm mt-1 line-clamp-2">{invoice.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatsCard: React.FC<{
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  className?: string;
}> = ({ title, value, subtitle, icon, className = "" }) => {
  return (
    <Card className={`transition-all hover:shadow-md ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-8 w-8 rounded-full bg-secondary/10 flex items-center justify-center">
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
};

// Helper function to get the appropriate currency icon based on country context
const getCurrencyIcon = (currencyCode?: string): React.ReactNode => {
  switch (currencyCode) {
    case 'USD':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'GBP':
    default:
      return <PoundSterling className="h-4 w-4 text-muted-foreground" />;
  }
};

export function InvoicesDashboard({
  onCreateInvoice,
  onViewInvoice,
  onEditInvoice,
}: {
  onCreateInvoice?: () => void;
  onViewInvoice?: (invoice: Invoice) => void;
  onEditInvoice?: (invoice: Invoice) => void;
}) {
  const { toast } = useToast();
  const { formatCurrency, currentCountry, contractorProfile, isLoadingProfile } = useCountry();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Debug logging for currency context
  useEffect(() => {
    console.log('[InvoicesDashboard] Country Context Debug:', {
      currentCountry: currentCountry,
      contractorProfile: contractorProfile,
      currencyCode: currentCountry?.currencyCode,
      currencySymbol: currentCountry?.currencySymbol,
      isLoadingProfile: isLoadingProfile
    });
  }, [currentCountry, contractorProfile, isLoadingProfile]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      const response = await InvoiceAPIService.getInvoices();
      setInvoices(response.invoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast({
        title: "Error",
        description: "Failed to load invoices. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvoice = () => {
    if (onCreateInvoice) {
      onCreateInvoice();
    } else {
      // Fallback for when used without container
      toast({
        title: "Coming Soon",
        description: "Invoice creation will be available soon.",
      });
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    if (onViewInvoice) {
      onViewInvoice(invoice);
    } else {
      // Fallback for when used without container
      toast({
        title: "View Invoice",
        description: `Viewing invoice ${invoice.invoiceNumber || invoice.id}`,
      });
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    if (onEditInvoice) {
      onEditInvoice(invoice);
    } else {
      // Fallback for when used without container
      toast({
        title: "Edit Invoice",
        description: `Editing invoice ${invoice.invoiceNumber || invoice.id}`,
      });
    }
  };

  const handleDeleteInvoice = async (invoice: Invoice) => {
    try {
      await InvoiceAPIService.deleteInvoice(invoice.id);
      toast({
        title: "Success",
        description: "Invoice deleted successfully.",
      });
      fetchInvoices(); // Refresh the list
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast({
        title: "Error",
        description: "Failed to delete invoice. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      await InvoiceAPIService.downloadInvoiceDocument(invoice.id);
      toast({
        title: "Success",
        description: "Invoice PDF downloaded successfully.",
      });
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast({
        title: "Error",
        description: "Failed to download invoice PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Calculate stats
  const totalInvoices = invoices.length;
  const draftInvoices = invoices.filter((inv) => inv.status === "DRAFT").length;
  const pendingInvoices = invoices.filter(
    (inv) => inv.status === "SUBMITTED",
  ).length;
  const totalValue = invoices.reduce(
    (sum, inv) => sum + (parseFloat(inv.grandTotal.toString()) || 0),
    0,
  );

  const headerContent = (
    <Button onClick={handleCreateInvoice} className="w-full sm:w-auto" data-testid="create-invoice-button">
      <Plus className="h-4 w-4 mr-2" />
      Create Invoice
    </Button>
  );

  if (isLoading || isLoadingProfile) {
    return (
      <DashboardContainer title="Invoices" headerContent={headerContent}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer title="Invoices" headerContent={headerContent}>
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatsCard
          title="Total Invoices"
          value={totalInvoices.toString()}
          subtitle={`${totalInvoices} invoice${totalInvoices !== 1 ? "s" : ""} created`}
          icon={<FileText className="h-4 w-4 text-muted-foreground" />}
        />
        <StatsCard
          title="Draft Invoices"
          value={draftInvoices.toString()}
          subtitle={`${draftInvoices} draft${draftInvoices !== 1 ? "s" : ""} pending`}
          icon={<Edit className="h-4 w-4 text-muted-foreground" />}
        />
        <StatsCard
          title="Pending Approval"
          value={pendingInvoices.toString()}
          subtitle={`${pendingInvoices} awaiting approval`}
          icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
        />
        <StatsCard
          title="Total Value"
          value={formatCurrency(totalValue)}
          subtitle="Combined invoice value"
          icon={getCurrencyIcon(currentCountry?.currencyCode)}
        />
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ReceiptText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No invoices yet</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Get started by creating your first invoice. You can add line
              items, set due dates, and track payments.
            </p>
            <Button onClick={handleCreateInvoice} data-testid="create-first-invoice-button">
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-lg font-semibold">
              All Invoices ({invoices.length})
            </h3>
          </div>
          <div className="grid gap-4">
            {invoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onView={handleViewInvoice}
                onEdit={handleEditInvoice}
                onDelete={handleDeleteInvoice}
                onDownload={handleDownloadInvoice}
              />
            ))}
          </div>
        </div>
      )}
    </DashboardContainer>
  );
}
